const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

class MxDrawer extends HTMLElement {
  #lastFocused = null;

  connectedCallback() {
    this.toggle = this.querySelector('[data-drawer-toggle]');
    this.panel = this.querySelector('.mx-drawer__panel');
    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.lists = [...this.querySelectorAll('[data-drawer-list]')];

    this.toggle?.addEventListener('click', () => this.open());
    this.overlay?.addEventListener('click', () => this.close());
    this.querySelector('[data-drawer-close]')?.addEventListener('click', () => this.close());

    this.querySelectorAll('[data-drawer-open]').forEach((button) => {
      button.addEventListener('click', () => this.showList(button.dataset.drawerOpen, button));
    });

    this.querySelectorAll('[data-drawer-back]').forEach((button) => {
      button.addEventListener('click', () => this.showList('root'));
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
      if (event.key === 'Tab') this.#trapFocus(event);
    });

    this.showList('root');
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  open() {
    if (this.isOpen) return;

    this.#lastFocused = document.activeElement;
    this.setAttribute('open', '');
    this.toggle?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('mx-drawer-open');

    // Move focus to the dialog container, not the first link: focusing an
    // interactive element here paints a :focus-visible ring (e.g. the bold
    // outline on the first mega-menu card) that reads as a stale selection.
    // The panel is role="dialog" tabindex="-1", so focus enters the modal
    // without a ring, and Tab still steps through the content normally.
    requestAnimationFrame(() => this.panel?.focus());
  }

  close() {
    if (!this.isOpen) return;

    this.removeAttribute('open');
    this.toggle?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('mx-drawer-open');
    this.showList('root');
    this.#lastFocused?.focus();
  }

  showList(name, opener) {
    this.lists.forEach((list) => {
      list.toggleAttribute('active', list.dataset.drawerList === name);
    });

    this.querySelectorAll('[data-drawer-open]').forEach((button) => {
      button.setAttribute('aria-expanded', String(button === opener));
    });

    if (!this.isOpen) return;

    const target = this.lists.find((list) => list.dataset.drawerList === name);
    target?.querySelector(FOCUSABLE)?.focus();
  }

  #focusable() {
    return [...this.panel.querySelectorAll(FOCUSABLE)].filter(
      (element) => element.offsetParent !== null && getComputedStyle(element).visibility !== 'hidden'
    );
  }

  #trapFocus(event) {
    if (!this.isOpen) return;

    const items = this.#focusable();
    if (items.length === 0) return;

    const first = items[0];
    const last = items.at(-1);

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define('mx-drawer', MxDrawer);

class MxCartCount extends HTMLElement {
  connectedCallback() {
    this.cartLink = this.closest('.mx-header__icon--cart');
    this.baseLabel = this.cartLink?.dataset.mxCartLabel || this.cartLink?.getAttribute('aria-label') || 'Cart';
    this.needsSync = false;
    this.sync = this.sync.bind(this);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.sync);
    }
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  sync() {
    this.needsSync = true;
    if (this.request) return this.request;

    this.request = this.#refreshUntilCurrent().finally(() => {
      this.request = null;
    });

    return this.request;
  }

  async #refreshUntilCurrent() {
    while (this.needsSync) {
      this.needsSync = false;

      try {
        const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
        const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });

        if (!response.ok) return;

        const cart = await response.json();
        const count = cart.item_count;

        this.#render(count);
      } catch {
        return;
      }
    }
  }

  #render(count) {
    const displayCount = count < 100 ? String(count) : '99+';

    this.textContent = displayCount;
    this.toggleAttribute('hidden', count === 0);
    this.cartLink?.setAttribute('aria-label', count > 0 ? `${this.baseLabel} (${count})` : this.baseLabel);
  }
}

customElements.define('mx-cart-count', MxCartCount);

class MxHeaderSearch extends HTMLElement {
  connectedCallback() {
    this.mobileQuery = window.matchMedia('(max-width: 575px)');
    this.searchInput = this.querySelector('.mx-header__search-input');
    this.clearButton = this.querySelector('.mx-header__search-clear');
    this.frame = null;
    this.touchY = null;
    this.onScroll = () => {
      if (this.frame !== null) return;

      this.frame = window.requestAnimationFrame(() => this.#syncPosition());
    };
    this.onWheel = (event) => this.#handleDirection(event.deltaY);
    this.onTouchStart = (event) => {
      this.touchY = event.touches[0]?.clientY ?? null;
    };
    this.onTouchMove = (event) => {
      const currentY = event.touches[0]?.clientY;

      if (this.touchY === null || currentY === undefined) return;

      const scrollDelta = this.touchY - currentY;
      if (Math.abs(scrollDelta) > 8) {
        this.#handleDirection(scrollDelta);
        this.touchY = currentY;
      }
    };
    this.onTouchEnd = () => {
      this.touchY = null;
    };
    this.onBreakpointChange = () => this.#syncPosition();
    this.onFocusIn = () => this.#applyState(window.scrollY > 4, false);
    this.onClear = () => {
      if (!this.searchInput) return;

      this.searchInput.value = '';
      this.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.searchInput.focus();
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('wheel', this.onWheel, { passive: true });
    window.addEventListener('touchstart', this.onTouchStart, { passive: true });
    window.addEventListener('touchmove', this.onTouchMove, { passive: true });
    window.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.mobileQuery.addEventListener('change', this.onBreakpointChange);
    this.addEventListener('focusin', this.onFocusIn);
    this.clearButton?.addEventListener('click', this.onClear);
    this.#syncPosition();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('touchstart', this.onTouchStart);
    window.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('touchend', this.onTouchEnd);
    this.mobileQuery?.removeEventListener('change', this.onBreakpointChange);
    this.removeEventListener('focusin', this.onFocusIn);
    this.clearButton?.removeEventListener('click', this.onClear);

    if (this.frame !== null) window.cancelAnimationFrame(this.frame);
  }

  #syncPosition() {
    this.frame = null;

    const currentScroll = Math.max(window.scrollY, 0);
    const isActive = this.mobileQuery.matches;

    if (!isActive) {
      this.#applyState(false, false);
      return;
    }

    const isSticky = currentScroll > 4;
    const shouldHide =
      isSticky &&
      !this.contains(document.activeElement) &&
      (this.hasAttribute('data-non-sticky') || this.hasAttribute('data-hidden'));
    this.#applyState(isSticky, shouldHide);
  }

  #handleDirection(scrollDelta) {
    if (!this.mobileQuery.matches) return;
    if (window.scrollY <= 4 || this.contains(document.activeElement)) return;

    if (this.hasAttribute('data-non-sticky')) {
      this.#applyState(true, true);
      return;
    }

    if (scrollDelta > 8) this.#applyState(true, true);
    if (scrollDelta < -8) this.#applyState(true, false);
  }

  #applyState(sticky, hidden) {
    this.toggleAttribute('data-sticky', sticky);
    this.toggleAttribute('data-hidden', hidden);
    this.toggleAttribute('inert', hidden);
  }
}

customElements.define('mx-header-search', MxHeaderSearch);

const GROWAVE_HEADER_STYLE_ID = 'mx-growave-header-overrides';
const GROWAVE_HEADER_CONTAINER_CLASS = 'mx-growave-header-container';
const GROWAVE_HEADER_STYLE = `
  .${GROWAVE_HEADER_CONTAINER_CLASS} {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .gw-wl-go-to-wishlist-header-widget {
    width: auto !important;
    height: auto !important;
    bottom: auto !important;
    right: auto !important;
  }

  .gw-wl-go-to-wishlist-header-widget::after {
    color: #0a0a0a !important;
    width: 1.5rem !important;
    height: 1.5rem !important;
    top: -0.8rem !important;
    right: -0.6rem !important;
    bottom: auto !important;
    left: auto !important;
  }

  .gw-wl-go-to-wishlist-header-widget[data-items-count='0']::after {
    display: none !important;
  }

`;

class MxWishlist extends HTMLElement {
  connectedCallback() {
    this.placeholder = this.querySelector('.gw-wl-header-icon-placeholder');
    this.applyGrowaveOverrides = () => {
      this.placeholder = this.placeholder || this.querySelector('.gw-wl-header-icon-placeholder');
      const shadowRoot = this.placeholder?.shadowRoot;
      if (!shadowRoot) return false;

      if (!shadowRoot.getElementById(GROWAVE_HEADER_STYLE_ID)) {
        const style = document.createElement('style');
        style.id = GROWAVE_HEADER_STYLE_ID;
        style.textContent = GROWAVE_HEADER_STYLE;
        shadowRoot.append(style);
      }

      if (!this.shadowObserver) {
        this.shadowObserver = new MutationObserver(() => this.applyGrowaveOverrides());
        this.shadowObserver.observe(shadowRoot, { childList: true, subtree: true });
      }

      const widget = shadowRoot.querySelector('.gw-wl-go-to-wishlist-header-widget');
      const iconSrc = this.dataset.iconSrc;

      widget?.parentElement?.classList.add(GROWAVE_HEADER_CONTAINER_CLASS);

      if (widget && iconSrc && !widget.querySelector('[data-mx-wishlist-icon]')) {
        const iconSize = Number(this.dataset.iconSize) || 22;
        const icon = new Image(iconSize, iconSize);
        icon.src = iconSrc;
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        icon.setAttribute('data-mx-wishlist-icon', '');
        icon.style.display = 'block';
        icon.style.objectFit = 'contain';
        widget.replaceChildren(icon);
      }

      if (widget) {
        const icon = widget.querySelector('[data-mx-wishlist-icon]');

        if (!icon || icon.complete) {
          this.setAttribute('data-ready', '');
        } else {
          icon.addEventListener('load', () => this.setAttribute('data-ready', ''), { once: true });
          icon.addEventListener('error', () => this.setAttribute('data-ready', ''), { once: true });
        }
      }

      this.documentObserver?.disconnect();
      return true;
    };

    if (this.applyGrowaveOverrides()) return;

    // Growave creates the widget asynchronously inside its shadow DOM.
    this.documentObserver = new MutationObserver(() => this.applyGrowaveOverrides());
    this.documentObserver.observe(document.documentElement, { childList: true, subtree: true });
    window.addEventListener('load', this.applyGrowaveOverrides, { once: true });
  }

  disconnectedCallback() {
    this.documentObserver?.disconnect();
    this.shadowObserver?.disconnect();
    this.documentObserver = null;
    this.shadowObserver = null;
    this.placeholder = null;
    window.removeEventListener('load', this.applyGrowaveOverrides);
  }
}

customElements.define('mx-wishlist', MxWishlist);

/* Native cart drawer controller for product and collection actions. */
const MxCart = {
  activeDrawer: null,
  /* Adds in flight that should update the cart without opening the drawer. */
  quietAdds: 0,
  drawerObserver: null,
  previewRepairTimer: null,
  observingCartMutations: false,

  get drawer() {
    return document.querySelector('cart-drawer');
  },

  activateDrawer() {
    const drawer = this.drawer;
    if (!drawer || this.activeDrawer === drawer) return;

    this.activeDrawer = drawer;
    this.markCartIcon();
    this.observeDrawer(drawer);
    this.observeCartMutations();
    document.dispatchEvent(new CustomEvent('native-cart-drawer:ready'));
  },

  markCartIcon() {
    const link = document.querySelector('.mx-header__icon--cart');
    if (link && this.drawer) link.setAttribute('aria-haspopup', 'dialog');
  },

  refreshCount() {
    document.querySelectorAll('mx-cart-count').forEach((counter) => counter.sync());
  },

  observeDrawer(drawer) {
    this.drawerObserver?.disconnect();
    this.drawerObserver = new MutationObserver(() => {
      document.dispatchEvent(new CustomEvent('native-cart-drawer:updated'));
      this.repairStalePreviewDrawer(drawer);
    });
    this.drawerObserver.observe(drawer, { childList: true, subtree: true });
  },

  repairStalePreviewDrawer(drawer) {
    if (window.Shopify?.theme?.role === 'main') return;
    if (drawer.querySelector('[data-mx-native-cart-content]')) return;
    if (!drawer.querySelector('.cart-item')) return;

    clearTimeout(this.previewRepairTimer);
    this.previewRepairTimer = setTimeout(() => {
      if (!drawer.isConnected || drawer.querySelector('[data-mx-native-cart-content]')) return;
      this.refreshAndOpen();
    }, 50);
  },

  observeCartMutations() {
    if (this.observingCartMutations) return;
    this.observingCartMutations = true;

    const cart = this;
    const cartMutation = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

    const describe = (url, body) => {
      const path = String(url || '');
      if (!cartMutation.test(path)) return null;

      let handled = false;
      try {
        if (body instanceof FormData) {
          handled = body.has('sections');
        } else if (typeof body === 'string' && body.length) {
          handled = body.includes('"sections"') || body.includes('sections=');
        } else if (body && typeof body === 'object' && 'sections' in body) {
          handled = true;
        }
      } catch {
        handled = false;
      }

      return { isAdd: /\/cart\/add/.test(path), handled };
    };

    const react = async (info) => {
      if (!info || info.handled || !cart.drawer) return;

      if (info.isAdd && cart.quietAdds > 0) {
        await cart.refresh();
        return;
      }

      // Apps can update cart attributes during page load. Dawn's
      // renderContents() always schedules open(), so refreshing a closed drawer
      // here would make it appear without a shopper action.
      if (!info.isAdd && !cart.drawer.classList.contains('active')) return;

      await cart.refreshAndOpen();

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mx-cart-observer' });
      }
    };

    const nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input?.url;
      const info = describe(url, init?.body);

      return nativeFetch.apply(this, arguments).then((response) => {
        if (info && response.ok) {
          cart.refreshCount();
          react(info);
        }
        return response;
      });
    };

    const nativeOpen = window.XMLHttpRequest.prototype.open;
    const nativeSend = window.XMLHttpRequest.prototype.send;
    const requestUrls = new WeakMap();

    window.XMLHttpRequest.prototype.open = function (method, url) {
      requestUrls.set(this, url);
      return nativeOpen.apply(this, arguments);
    };

    window.XMLHttpRequest.prototype.send = function (body) {
      const info = describe(requestUrls.get(this), body);

      if (info) {
        this.addEventListener(
          'load',
          () => {
            if (this.status >= 200 && this.status < 300) {
              cart.refreshCount();
              react(info);
            }
          },
          { once: true },
        );
      }

      return nativeSend.apply(this, arguments);
    };
  },

  /*
   * Dawn's renderContents() expects the Section Rendering API payload shape and
   * opens the drawer itself, so refreshing and opening is a single call.
   */
  async refreshAndOpen(trigger, openImmediately = false) {
    const drawer = this.drawer;
    if (!drawer) return false;

    if (trigger) drawer.setActiveElement(trigger);
    if (openImmediately) drawer.open();

    const ids = drawer.getSectionsToRender().map((section) => section.id);
    const url = `${window.routes?.cart_url || '/cart'}?sections=${ids.join(',')}`;

    try {
      /*
       * No Accept: application/json here — that header makes /cart answer with
       * the cart JSON instead of the Section Rendering API payload, which left
       * the drawer with nothing to render and silently refusing to open.
       */
      const response = await fetch(url);
      if (!response.ok) return false;

      drawer.renderContents({ sections: await response.json() });
      this.syncEmptyState();
      return true;
    } catch {
      return false;
    }
  },

  /*
   * Dawn's renderContents() clears .is-empty off .drawer__inner but leaves it on
   * the <cart-drawer> host; product-form.js clears the host separately. open()
   * reads the host class to pick the focus-trap container, so a stale .is-empty
   * makes it trap on a .drawer__inner-empty that no longer exists and throw.
   */
  syncEmptyState() {
    const drawer = this.drawer;
    if (!drawer) return;

    drawer.classList.toggle('is-empty', !!drawer.querySelector('.drawer__inner-empty'));
  },

  /*
   * Shopify preview themes can return section HTML from the live theme inside
   * cart mutation responses. Refreshing through the Section Rendering API keeps
   * development and unpublished previews on their actual theme; live keeps the
   * faster single-response path.
   */
  async renderMutationResult(result, trigger) {
    const canUseReturnedSections = window.Shopify?.theme?.role === 'main';

    if (canUseReturnedSections && result.sections && this.drawer) {
      this.drawer.setActiveElement(trigger || document.activeElement);
      this.drawer.renderContents(result);
      this.syncEmptyState();
      return;
    }

    await this.refreshAndOpen(trigger);
  },

  /*
   * Same section refresh as refreshAndOpen(), rendered without Dawn's
   * renderContents() because that always opens the drawer.
   */
  async refresh() {
    const drawer = this.drawer;
    if (!drawer) return false;

    const sectionsToRender = drawer.getSectionsToRender();
    const url = `${window.routes?.cart_url || '/cart'}?sections=${sectionsToRender.map((section) => section.id).join(',')}`;

    try {
      const response = await fetch(url);
      if (!response.ok) return false;

      const sections = await response.json();
      sectionsToRender.forEach((section) => {
        const element = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        if (element && sections[section.id]) {
          element.innerHTML = drawer.getSectionInnerHTML(sections[section.id], section.selector);
        }
      });
      drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
      this.syncEmptyState();
      this.refreshCount();
      return true;
    } catch {
      return false;
    }
  },

  async add(items, trigger, { open = true } = {}) {
    const body = {
      items,
      sections: this.drawer?.getSectionsToRender().map((s) => s.id),
      sections_url: window.location.pathname,
    };
    const response = await window.fetch(`${window.routes?.cart_add_url || '/cart/add'}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Cart error');

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mx-cart', cartData: result });
    }

    if (open) {
      await this.renderMutationResult(result, trigger);
    } else {
      await this.refresh();
    }

    return result;
  },
  /* Line-item change (quantity, including quantity: 0 to remove). */
  async change(body, trigger) {
    const payload = {
      ...body,
      sections: this.drawer?.getSectionsToRender().map((s) => s.id),
      sections_url: window.location.pathname,
    };
    const response = await fetch(`${window.routes?.cart_change_url || '/cart/change'}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Cart error');

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mx-cart', cartData: result });
    }

    await this.renderMutationResult(result, trigger);

    return result;
  },
};

window.MxCart = MxCart;

/*
 * Header cart icon. Kept as a real <a href="/cart"> so it still works without
 * JS; the drawer only takes over once Dawn's component is on the page.
 */
document.addEventListener('click', async (event) => {
  const trigger = event.target.closest('.mx-header__icon--cart');
  if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  if (!MxCart.drawer) return;

  event.preventDefault();
  MxCart.refreshAndOpen(trigger);
}, true);

const initializeNativeCartDrawer = () => {
  if (MxCart.drawer) MxCart.activateDrawer();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeNativeCartDrawer, { once: true });
} else {
  initializeNativeCartDrawer();
}

/*
 * Cart drawer add-ons.
 *
 * The upsell <select> rewrites its form's variant input. Its submit uses
 * MxCart.add() so the native drawer receives and renders the returned sections.
 * The protection toggle uses the same helpers and lets the re-rendered drawer
 * supply the new state rather than tracking it in JS.
 */
document.addEventListener('submit', async (event) => {
  const form = event.target.closest('.cart-drawer-upsell__form form');
  if (!form) return;

  event.preventDefault();
  event.stopPropagation();

  const button = form.querySelector('[type="submit"]');
  if (!button || button.getAttribute('aria-busy') === 'true') return;

  const variantId = Number(form.elements.id?.value);
  const quantity = Number(form.elements.quantity?.value) || 1;
  if (!variantId) return;

  const spinner = button.querySelector('.loading__spinner');
  const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
  const errorMessage = errorWrapper?.querySelector('.product-form__error-message');

  button.setAttribute('aria-busy', 'true');
  button.setAttribute('aria-disabled', 'true');
  button.classList.add('loading');
  spinner?.classList.remove('hidden');
  errorWrapper?.setAttribute('hidden', '');

  try {
    await MxCart.add([{ id: variantId, quantity }], button);
  } catch (error) {
    console.error(error);
    if (errorWrapper && errorMessage) {
      errorMessage.textContent = error.message;
      errorWrapper.removeAttribute('hidden');
    }
  } finally {
    if (button.isConnected) {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
      spinner?.classList.add('hidden');
    }
  }
}, true);

document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-cart-drawer-upsell-select]');
  if (!select) return;

  const input = select.closest('form')?.querySelector('[data-cart-drawer-upsell-input]');
  if (input) input.value = select.value;
});

document.addEventListener('click', async (event) => {
  const toggle = event.target.closest('[data-cart-drawer-protection]');
  if (!toggle || toggle.getAttribute('aria-busy') === 'true') return;

  event.preventDefault();

  const adding = toggle.getAttribute('aria-checked') !== 'true';
  const lineKey = toggle.dataset.lineKey;
  const variantId = Number(toggle.dataset.variantId);

  // Reflect the intent immediately; the re-render is the source of truth.
  toggle.setAttribute('aria-checked', String(adding));
  toggle.setAttribute('aria-busy', 'true');

  try {
    if (adding) {
      await MxCart.add([{ id: variantId, quantity: 1 }], toggle);
    } else if (lineKey) {
      await MxCart.change({ id: lineKey, quantity: 0 }, toggle);
    }
  } catch (error) {
    console.error(error);
    toggle.setAttribute('aria-checked', String(!adding));
  } finally {
    toggle.removeAttribute('aria-busy');
  }
});

document.addEventListener('click', (event) => {
  if (event.target.closest('.cart-drawer-protection__info')) return;

  document.querySelectorAll('.cart-drawer-protection__info[open]').forEach((tooltip) => {
    tooltip.removeAttribute('open');
  });
});

/*
 * "Others also bought" arrows. The list is a scroll-snap row, so paging is just
 * a scroll by one card width; disabled state is derived from scroll position so
 * it stays right after the drawer re-renders.
 */
document.addEventListener('click', (event) => {
  const arrow = event.target.closest('[data-cart-drawer-upsell-prev], [data-cart-drawer-upsell-next]');
  if (!arrow) return;

  const list = arrow.closest('.cart-drawer-upsell')?.querySelector('.cart-drawer-upsell__list');
  const card = list?.querySelector('.cart-drawer-upsell__item');
  if (!list || !card) return;

  const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(list).columnGap || 0);
  const back = arrow.hasAttribute('data-cart-drawer-upsell-prev');

  list.scrollBy({ left: back ? -step : step, behavior: 'smooth' });
});

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-cart-drawer-upsell-toggle], [data-cart-drawer-disclosure-toggle]');
  if (!toggle) return;

  const disclosure = getCartDrawerDisclosure(toggle);
  if (!disclosure?.content) return;

  const expanded = toggle.getAttribute('aria-expanded') === 'true';
  applyCartDrawerDisclosureState(disclosure, expanded);
  setCartDrawerDisclosureCollapsed(disclosure.storageKey, expanded);

  if (!expanded && disclosure.list) requestAnimationFrame(() => syncUpsellArrows(disclosure.list));
});

const upsellCollapsedKey = 'mx-cart-upsell-collapsed';

const setCartDrawerDisclosureCollapsed = (storageKey, collapsed) => {
  try {
    sessionStorage.setItem(storageKey, String(collapsed));
  } catch {
    // The current DOM state still works when storage is unavailable.
  }
};

const isCartDrawerDisclosureCollapsed = (storageKey) => {
  try {
    return sessionStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
};

const getCartDrawerDisclosure = (toggle) => {
  const upsell = toggle.closest('.cart-drawer-upsell');
  if (upsell) {
    return {
      toggle,
      content: upsell.querySelector('.cart-drawer-upsell__content'),
      navigation: upsell.querySelector('.cart-drawer-upsell__nav'),
      list: upsell.querySelector('.cart-drawer-upsell__list'),
      storageKey: upsellCollapsedKey,
    };
  }

  const contentId = toggle.getAttribute('aria-controls');
  const owner = toggle.closest('[data-cart-drawer-shipping-policy]');
  const content = owner?.querySelector('[data-cart-drawer-disclosure-content]');
  if (content?.id !== contentId) return null;
  if (!owner || !content) return null;

  return {
    toggle,
    content,
    navigation: null,
    list: null,
    storageKey: toggle.dataset.cartDrawerDisclosureKey,
  };
};

const applyCartDrawerDisclosureState = (disclosure, collapsed) => {
  disclosure.content.hidden = collapsed;
  if (disclosure.navigation) disclosure.navigation.hidden = collapsed;
  disclosure.toggle.setAttribute('aria-expanded', String(!collapsed));
};

const syncUpsellCollapsedState = (upsell) => {
  const toggle = upsell.querySelector('[data-cart-drawer-upsell-toggle]');
  if (!toggle) return;

  const disclosure = getCartDrawerDisclosure(toggle);
  if (!disclosure?.content) return;
  applyCartDrawerDisclosureState(disclosure, isCartDrawerDisclosureCollapsed(disclosure.storageKey));
};

const syncShippingPolicyCollapsedState = (policy) => {
  const toggle = policy.querySelector('[data-cart-drawer-disclosure-toggle]');
  if (!toggle) return;

  const disclosure = getCartDrawerDisclosure(toggle);
  if (!disclosure?.content || !disclosure.storageKey) return;
  applyCartDrawerDisclosureState(disclosure, isCartDrawerDisclosureCollapsed(disclosure.storageKey));
};

const syncUpsellArrows = (list) => {
  const bar = list.closest('.cart-drawer-upsell');
  const prev = bar?.querySelector('[data-cart-drawer-upsell-prev]');
  const next = bar?.querySelector('[data-cart-drawer-upsell-next]');
  if (!prev || !next) return;

  const max = list.scrollWidth - list.clientWidth;
  prev.disabled = list.scrollLeft <= 1;
  next.disabled = list.scrollLeft >= max - 1;
};

document.addEventListener(
  'scroll',
  (event) => {
    const list = event.target.closest?.('.cart-drawer-upsell__list');
    if (list) syncUpsellArrows(list);
  },
  true
);

const syncAllUpsellArrows = () => {
  document.querySelectorAll('.cart-drawer-upsell__list').forEach((list) => {
    if (!list.closest('.cart-drawer-upsell__content')?.hidden) syncUpsellArrows(list);
  });
};

const syncAllUpsells = () => {
  document.querySelectorAll('.cart-drawer-upsell').forEach(syncUpsellCollapsedState);
  document.querySelectorAll('[data-cart-drawer-shipping-policy]').forEach(syncShippingPolicyCollapsedState);
  syncAllUpsellArrows();
};

syncAllUpsells();
document.addEventListener('native-cart-drawer:ready', syncAllUpsells);
document.addEventListener('native-cart-drawer:updated', syncAllUpsells);
